import { useEffect, useCallback, useRef } from "react";
import { getNewToken } from "@/graphql/client";

/**
 * Hook to handle token update event from chat widget
 * @param {boolean} tokenChecked - Flag indicating if the initial token check is completed
 */
const useWidgetTokenUpdate = (tokenChecked) => {
    const isRefreshing = useRef(false);

    const handleTokenUpdate = useCallback(async () => {
        // Prevent race condition: if already refreshing, don't start another one
        if (isRefreshing.current) {
            console.debug(
                "[useWidgetTokenUpdate] Token refresh already in progress, skipping event."
            );
            return;
        }

        console.debug(
            "[useWidgetTokenUpdate] WIDGET_TOKEN_UPDATE_NEEDED event received, refreshing token..."
        );
        isRefreshing.current = true;

        try {
            await getNewToken();
        } catch (error) {
            console.error(
                "[useWidgetTokenUpdate] Failed to refresh token:",
                error
            );
        } finally {
            isRefreshing.current = false;
        }
    }, []);

    useEffect(() => {
        // Only register the event handler when tokenChecked is true
        if (tokenChecked) {
            window.addEventListener(
                "WIDGET_TOKEN_UPDATE_NEEDED",
                handleTokenUpdate
            );
            console.debug(
                "[useWidgetTokenUpdate] Registered WIDGET_TOKEN_UPDATE_NEEDED listener."
            );

            return () => {
                window.removeEventListener(
                    "WIDGET_TOKEN_UPDATE_NEEDED",
                    handleTokenUpdate
                );
                console.debug(
                    "[useWidgetTokenUpdate] Unregistered WIDGET_TOKEN_UPDATE_NEEDED listener."
                );
            };
        }
    }, [tokenChecked, handleTokenUpdate]);
};

export default useWidgetTokenUpdate;
